/**
 * Limits the Shopify PDP variant picker to variants returned by Voyado.
 * Shopify remains the source of variant content, pricing and availability.
 */
(function initializeVoyadoProductPageVariants(window, document) {
    'use strict';

    const productPage = window.VOYADO_PRODUCT_PAGE;
    const context = window.VOYADO_CONTEXT?.productPage;
    const debug = window.VOYADO_ELEVATE?.debug || (() => {});

    if (!context?.enabled || !productPage) return;

    function voyadoVariantKeys(response) {
        return new Set(
            (response?.productGroup?.products || [])
                .flatMap((product) => product?.variants || [])
                .map((variant) => String(variant?.key || '').trim())
                .filter(Boolean)
        );
    }

    function shopifyVariantKey(variant) {
        const key = context.variantKeySource === 'sku'
            ? variant?.sku
            : variant?.id;

        return window.VOYADO_ELEVATE?.normalizeVariantKey(key, context.variantKeySource)
            ?? String(key || '').trim();
    }

    function allowedOptionValues(variants, selectedOptions, optionIndex) {
        return new Set(
            variants
                .filter((variant) => selectedOptions
                    .slice(0, optionIndex)
                    .every((value, index) => variant.options[index] === value))
                .map((variant) => variant.options[optionIndex])
        );
    }

    function setSelectedOption(picker, optionIndex, value) {
        if (picker.matches('variant-radios')) {
            const fieldset = picker.querySelectorAll('fieldset')[optionIndex];
            const input = [...(fieldset?.querySelectorAll('input[type="radio"]') || [])]
                .find((candidate) => candidate.value === value);
            if (input) input.checked = true;
            return;
        }

        const dropdown = picker.querySelectorAll(
            '.product-variant-dropdown'
        )[optionIndex];
        const item = [...(dropdown?.querySelectorAll('li[data-option-value]') || [])]
            .find((candidate) => candidate.dataset.optionValue === value);

        dropdown?.querySelectorAll('li[data-option-value]').forEach((candidate) => {
            candidate.dataset.selected = String(candidate === item);
        });
        if (item) picker.updateButton(item, dropdown);
    }

    function constrainOptions(picker, variants) {
        const selectedOptions = [...picker.options];

        selectedOptions.forEach((selectedValue, optionIndex) => {
            const allowedValues = allowedOptionValues(
                variants,
                selectedOptions,
                optionIndex
            );

            if (!allowedValues.has(selectedValue)) {
                const fallbackValue = allowedValues.values().next().value;
                if (fallbackValue === undefined) return;
                selectedOptions[optionIndex] = fallbackValue;
                setSelectedOption(picker, optionIndex, fallbackValue);
            }

            const fieldset = picker.querySelectorAll('fieldset')[optionIndex];
            fieldset?.querySelectorAll('input[type="radio"]').forEach((input) => {
                const visible = allowedValues.has(input.value);
                input.hidden = !visible;
                const label = input.nextElementSibling?.matches('label')
                    ? input.nextElementSibling
                    : null;
                if (label) label.hidden = !visible;
            });

            const dropdown = picker.querySelectorAll(
                '.product-variant-dropdown'
            )[optionIndex];
            dropdown?.querySelectorAll('li[data-option-value]').forEach((item) => {
                item.hidden = !allowedValues.has(item.dataset.optionValue);
            });
        });
    }

    function constrainPicker(picker, variants) {
        const updateOptions = picker.updateOptions.bind(picker);

        picker.updateOptions = () => {
            updateOptions();
            constrainOptions(picker, variants);
            updateOptions();
        };
    }

    function selectVariant(picker, variant) {
        if (picker.matches('variant-radios')) {
            picker.querySelectorAll('fieldset').forEach((fieldset, index) => {
                const input = [...fieldset.querySelectorAll('input[type="radio"]')]
                    .find((candidate) => candidate.value === variant.options[index]);
                if (input) input.checked = true;
            });
            return picker.querySelector('input[type="radio"]:checked');
        }

        if (picker.matches('product-variant-dropdown')) {
            picker.querySelectorAll('.product-variant-dropdown').forEach((dropdown, index) => {
                const item = [...dropdown.querySelectorAll('li[data-option-value]')]
                    .find((candidate) => candidate.dataset.optionValue === variant.options[index]);

                dropdown.querySelectorAll('li[data-option-value]').forEach((candidate) => {
                    candidate.dataset.selected = String(candidate === item);
                });
                if (item) picker.updateButton(item, dropdown);
            });
            return picker.querySelector('li[data-selected="true"]');
        }

        return null;
    }

    function disableProductForms() {
        document.querySelectorAll(
            '.main-product product-form, product-buy-bar product-form'
        ).forEach((productForm) => {
            const button = productForm.querySelector('[name="add"]');
            if (button) button.disabled = true;
        });
    }

    async function applyVoyadoVariants(response) {
        const allowedKeys = voyadoVariantKeys(response);

        if (!allowedKeys.size) {
            debug('Product-page variants skipped', {
                reason: 'Voyado returned no variants',
            });
            return;
        }

        await Promise.all([
            customElements.whenDefined('variant-radios'),
            customElements.whenDefined('product-variant-dropdown'),
        ]);

        const pickers = document.querySelectorAll(
            '.main-product variant-radios, .main-product product-variant-dropdown'
        );
        let matchedVariantCount = 0;

        pickers.forEach((picker) => {
            const shopifyVariants = JSON.parse(
                picker.querySelector('script[type="application/json"]')?.textContent || '[]'
            );
            const variants = shopifyVariants.filter((variant) => (
                allowedKeys.has(shopifyVariantKey(variant))
            ));

            if (!variants.length) return;

            matchedVariantCount = Math.max(matchedVariantCount, variants.length);
            const variantData = picker.querySelector(
                'script[type="application/json"]'
            );
            if (variantData) variantData.textContent = JSON.stringify(variants);
            picker.variantData = variants;
            constrainPicker(picker, variants);

            picker.updateOptions();
            picker.updateMasterId();
            if (picker.currentVariant) {
                picker.updateVariantStatuses();
                return;
            }

            const selectedVariant = variants.find((variant) => variant.available) || variants[0];
            const target = selectVariant(picker, selectedVariant);
            if (target) picker.onVariantChange({ target });
        });

        if (!pickers.length) {
            const selectedVariantKey = window.VOYADO_ELEVATE?.normalizeVariantKey(
                context.selectedVariantKey,
                context.variantKeySource
            ) ?? String(context.selectedVariantKey || '').trim();
            const hasAllowedVariant = allowedKeys.has(selectedVariantKey);
            if (!hasAllowedVariant) disableProductForms();
        }

        debug('Applied product-page variants', {
            elevateVariantCount: allowedKeys.size,
            matchedShopifyVariantCount: matchedVariantCount,
        });
    }

    productPage.subscribe((state) => {
        if (state.status !== 'success') return;

        applyVoyadoVariants(state.response).catch((error) => {
            console.warn('[Voyado product page] Could not apply variants', error);
        });
    });
})(window, document);
